import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as fromPersonsActions from '../actions/persons.actions';
import { IPersonExportDto } from '../dtos/person-export-dto.interface';
import { IPersonExportTableHeaders } from '../dtos/person-export-table-header-dto.interface';
import { PersonsFilter } from '../interfaces/persons-filter.interface';
import * as fromPersons from '../reducer/persons.reducer';
import { PersonBulkUpdate } from '../services/persons.service';
import { IPersonsProvider } from './persons.provider.interface';
import { TasksUpdate } from '../enums/tasks-update.enum';

/**
 * REV-22155 — the only way the persons pages talk to the store: they call `dispatch…` here and
 * read the results through the `get…$` observables (page → provider → action → effect → service).
 */
@Injectable({
    providedIn: 'root',
})
export class PersonsProvider implements IPersonsProvider {
    constructor(private store: Store<fromPersons.PersonsFeatureState>, private actions$: Actions) {}

    dispatchPersonsList(): void {
        this.store.dispatch(fromPersonsActions.getPersons());
    }

    dispatchPersonsOfCompany(companyId: string): void {
        this.store.dispatch(fromPersonsActions.getPersonsOfCompany({ companyId }));
    }

    dispatchPersonById(id: string): void {
        this.store.dispatch(fromPersonsActions.getPersonById({ id }));
    }

    dispatchCreatePerson(person: IPersonDTO): void {
        this.store.dispatch(fromPersonsActions.createPerson({ person }));
    }

    dispatchUpdatePerson(person: IPersonDTO, tasksUpdate?: TasksUpdate): void {
        this.store.dispatch(fromPersonsActions.updatePerson({ person, tasksUpdate }));
    }

    dispatchUpdatePersonStatus(id: string, active: boolean): void {
        this.store.dispatch(fromPersonsActions.changePersonStatus({ id, active }));
    }

    dispatchDeletePerson(id: string): void {
        this.store.dispatch(fromPersonsActions.deletePerson({ id }));
    }

    dispatchBulkUpdatePersons(persons: PersonBulkUpdate[], tasksUpdate?: TasksUpdate): void {
        this.store.dispatch(fromPersonsActions.bulkUpdatePersons({ persons, tasksUpdate }));
    }

    dispatchSetPersonsFilter(filter: PersonsFilter): void {
        this.store.dispatch(fromPersonsActions.setPersonsFilter({ filter }));
    }

    dispatchExportPersonsDataAsExcel(
        tableHeaders: IPersonExportTableHeaders,
        persons: IPersonExportDto[],
        fileName: string,
    ): void {
        this.store.dispatch(
            fromPersonsActions.exportPersonsDataAsExcel.do({ payload: { tableHeaders, persons, fileName } }),
        );
    }

    resetPersons(): void {
        this.store.dispatch(fromPersonsActions.resetPersons());
    }

    resetPersonsErrors(): void {
        this.store.dispatch(fromPersonsActions.resetPersonsErrors());
    }

    getPersons$(): Observable<IPersonDTO[]> {
        return this.store.pipe(select(fromPersons.selectAllPersons));
    }

    getPersonsOfCompany$(): Observable<IPersonDTO[]> {
        return this.store.pipe(select(fromPersons.selectPersonsOfCompany));
    }

    getSelectedPerson$(): Observable<IPersonDTO | null> {
        return this.store.pipe(select(fromPersons.selectSelectedPerson));
    }

    getPersonsFilter$(): Observable<PersonsFilter> {
        return this.store.pipe(select(fromPersons.selectPersonsFilter));
    }

    getPersonsError$(): Observable<HttpErrorResponse | null> {
        return this.store.pipe(select(fromPersons.selectPersonsError));
    }

    /** Emits once a create or update finished — the page navigates or shows the error. */
    getSavePersonResult$() {
        return this.actions$.pipe(
            ofType(
                fromPersonsActions.onCreatePersonSuccessfully,
                fromPersonsActions.onCreatePersonFailed,
                fromPersonsActions.onUpdatePersonSuccessfully,
                fromPersonsActions.onUpdatePersonFailed,
            ),
        );
    }

    /** Emits once a delete finished — the page goes back to the overview or shows the error. */
    getDeletePersonResult$() {
        return this.actions$.pipe(
            ofType(fromPersonsActions.onDeletePersonSuccessfully, fromPersonsActions.onDeletePersonFailed),
        );
    }

    getBulkUpdatePersonsResult$() {
        return this.actions$.pipe(
            ofType(fromPersonsActions.onBulkUpdatePersonsSuccessfully, fromPersonsActions.onBulkUpdatePersonsFailed),
        );
    }
}
